/**
 * htmx layer over a VFS mount — the service worker as hypermedia backend
 * (sw-middleware-plugins, Phase 2).
 *
 * A wrapper plugin on the mount plugin's bindings, ordered just ahead of
 * it. It speaks only when asked for hypermedia: an `HX-Request: true`
 * fetch gets an HTML *fragment*; a plain navigation (`Accept: text/html`,
 * no HX header) gets the shell document, which loads htmx from the CDN
 * and `hx-get`s the same URL. Everything else (curl, PROPFIND, the
 * webdav client) returns null here and reaches the mount plugin untouched
 * — DAV stays protocol-pure.
 *
 * The six verbs, each a WebDAV call made *through* the mount plugin
 * (`mount.handle`), so credentials, path translation, and the 401
 * self-heal are never re-implemented:
 *
 *   GET dir/        → GET + X-Acequia-Directory-JSON → listing fragment
 *   GET file        → GET                            → editor (text) or info
 *   PUT file (form) → PUT [If-Match]                 → editor again, HX-Trigger: acequia:saved
 *   POST dir/ _action=mkcol name=   → MKCOL          → listing, HX-Trigger: acequia:created
 *   POST path _action=move destination= → MOVE       → listing, HX-Trigger: acequia:moved
 *   DELETE path     → DELETE                         → empty, HX-Reswap: delete
 *   (POST dir/ _action=create name= → PUT empty      → editor — the seventh, for a blank file)
 *
 * Fragments always answer 200 so htmx swaps them; an upstream failure is a
 * status fragment retargeted at #status. Lock semantics (open question 4):
 * optimistic concurrency via ETag/If-Match — the editor carries the ETag
 * it read, a 412 renders a conflict; no WebDAV LOCK.
 *
 * Cache policy per sidecar-cache-control's htmx section: every response
 * is `no-store` with `Vary: HX-Request, Accept`.
 */

export const HTMX_SRC = 'https://code.redfish.com/upstream/htmx/2.0.10/htmx.min.js'
export const HTMX_INTEGRITY = 'sha384-H5SrcfygHmAuTDZphMHqBJLc3FhssKjG7w/CeCpFReSfwBWDTKpkzPP8c+cLsK+V'
const MAX_EDIT_BYTES = 1024 * 1024

export function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

export function isHtmxRequest(request) {
    return request.headers.get('HX-Request') === 'true'
}

export function wantsHtmlDocument(request) {
    if (request.method !== 'GET') return false
    if (isHtmxRequest(request)) return false
    const accept = request.headers.get('Accept') || ''
    return accept.includes('text/html')
}

const TEXT_TYPES = /^(text\/|application\/(json|xml|javascript|ecmascript|x-yaml|yaml|toml|csv|x-sh|sql)|image\/svg)/i
const TEXT_EXT = /\.(txt|md|markdown|json|js|mjs|cjs|ts|css|html?|xml|svg|ya?ml|toml|csv|tsv|ini|cfg|conf|sh|py|rb|go|rs|c|h|cpp|java|sql|env|gitignore|log)$/i

export function isTextLike(contentType, pathname) {
    const ct = (contentType || '').split(';')[0].trim()
    if (ct && ct !== 'application/octet-stream') return TEXT_TYPES.test(ct)
    return TEXT_EXT.test(pathname)
}

// --- paths -----------------------------------------------------------------

const dirOf = (p) => (p.endsWith('/') ? p.replace(/[^/]+\/$/, '') : p.replace(/[^/]*$/, ''))
const baseOf = (p) => decodeURIComponent((p.endsWith('/') ? p.slice(0, -1) : p).split('/').pop() || '')
const encodeSegment = (name) => encodeURIComponent(name)

export function validName(name) {
    return typeof name === 'string' && name.length > 0 && name.length < 256 && !name.includes('/') && name !== '.' && name !== '..'
}

export function crumbs(pathname, root = '/') {
    const rootDir = root.endsWith('/') ? root : root + '/'
    const out = [{ href: rootDir, label: baseOf(rootDir) || '/' }]
    if (!pathname.startsWith(rootDir)) return out
    const rest = pathname.slice(rootDir.length)
    const parts = rest.split('/').filter(Boolean)
    let acc = rootDir
    parts.forEach((seg, i) => {
        const last = i === parts.length - 1 && !pathname.endsWith('/')
        acc += seg + (last ? '' : '/')
        out.push({ href: acc, label: decodeURIComponent(seg) })
    })
    return out
}

// --- templates ---------------------------------------------------------------

function fmtSize(n) {
    if (!(n >= 0)) return ''
    if (n < 1024) return `${n} B`
    const u = ['KB', 'MB', 'GB', 'TB']
    let i = -1
    do {
        n /= 1024
        i++
    } while (n >= 1024 && i < u.length - 1)
    return `${n.toFixed(1)} ${u[i]}`
}

function fmtDate(s) {
    const d = new Date(s)
    return isNaN(d) ? '' : d.toISOString().replace('T', ' ').slice(0, 16)
}

export function renderCrumbs(pathname, root) {
    const items = crumbs(pathname, root).map(
        (c, i, a) =>
            i === a.length - 1
                ? `<span class="crumb current">${escapeHtml(c.label)}</span>`
                : `<a class="crumb" href="${escapeHtml(c.href)}" hx-get="${escapeHtml(c.href)}" hx-target="#main" hx-push-url="true">${escapeHtml(c.label)}</a>`
    )
    return `<nav class="crumbs">${items.join('<span class="sep">/</span>')}</nav>`
}

export function renderRow(entry, dirPath) {
    const isDir = entry.type === 'directory'
    const name = entry.basename
    const href = dirPath + encodeSegment(name) + (isDir ? '/' : '')
    const h = escapeHtml(href)
    const n = escapeHtml(name)
    return (
        `<li class="row ${isDir ? 'dir' : 'file'}" data-path="${h}">` +
        `<a class="name" href="${h}" hx-get="${h}" hx-target="#main" hx-push-url="true">${isDir ? '&#128193;' : '&#128196;'} ${n}</a>` +
        `<span class="size">${isDir ? '' : escapeHtml(fmtSize(entry.size))}</span>` +
        `<span class="date">${escapeHtml(fmtDate(entry.lastmod))}</span>` +
        `<form class="rename" hx-post="${h}" hx-target="#main"><input type="hidden" name="_action" value="move"><input name="destination" placeholder="rename to…" required><button>Rename</button></form>` +
        `<button class="del" hx-delete="${h}" hx-target="closest li" hx-swap="delete" hx-confirm="Delete ${n}?">Delete</button>` +
        `</li>`
    )
}

export function renderListing({ path, entries, root, status = '' }) {
    const dir = path.endsWith('/') ? path : path + '/'
    const sorted = [...entries].sort((a, b) => (a.type === b.type ? a.basename.localeCompare(b.basename) : a.type === 'directory' ? -1 : 1))
    const rows = sorted.map((e) => renderRow(e, dir)).join('\n')
    const d = escapeHtml(dir)
    return (
        renderCrumbs(dir, root) +
        `<ul id="listing">${rows || '<li class="empty">(empty)</li>'}</ul>` +
        `<div class="actions">` +
        `<form class="mkcol" hx-post="${d}" hx-target="#main"><input type="hidden" name="_action" value="mkcol"><input name="name" placeholder="new folder" required><button>New folder</button></form>` +
        `<form class="newfile" hx-post="${d}" hx-target="#main"><input type="hidden" name="_action" value="create"><input name="name" placeholder="new file.md" required><button>New file</button></form>` +
        `</div>` +
        `<div id="status">${status}</div>`
    )
}

export function renderEditor({ path, text, etag, contentType, root, status = '' }) {
    const h = escapeHtml(path)
    const dir = escapeHtml(dirOf(path))
    return (
        renderCrumbs(path, root) +
        `<form class="editor" hx-put="${h}" hx-target="#main">` +
        `<input type="hidden" name="etag" value="${escapeHtml(etag || '')}">` +
        `<input type="hidden" name="contentType" value="${escapeHtml(contentType || 'text/plain; charset=utf-8')}">` +
        `<textarea name="content" spellcheck="false">${escapeHtml(text)}</textarea>` +
        `<div class="bar"><button>Save</button> <a href="${dir}" hx-get="${dir}" hx-target="#main" hx-push-url="true">Back</a></div>` +
        `</form>` +
        `<div id="status">${status}</div>`
    )
}

export function renderFileInfo({ path, size, contentType, root }) {
    const h = escapeHtml(path)
    const dir = escapeHtml(dirOf(path))
    return (
        renderCrumbs(path, root) +
        `<div class="fileinfo"><p><strong>${escapeHtml(baseOf(path))}</strong> — ${escapeHtml(contentType || 'unknown type')}${size >= 0 ? ', ' + escapeHtml(fmtSize(size)) : ''}</p>` +
        `<p>Not editable here. <a href="${h}" target="_blank" rel="noopener">Open</a> · <a href="${dir}" hx-get="${dir}" hx-target="#main" hx-push-url="true">Back</a></p></div>` +
        `<div id="status"></div>`
    )
}

export function renderStatus(message, kind = 'ok') {
    return `<span class="status ${escapeHtml(kind)}">${escapeHtml(message)}</span>`
}

export function renderShell({ path, title, htmxSrc = HTMX_SRC, htmxIntegrity = HTMX_INTEGRITY }) {
    const p = escapeHtml(path)
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title || path)}</title>
<style>
  body { font-family: system-ui, sans-serif; margin: 0; background: #14141f; color: #e6e6ee; }
  header { padding: .6rem 1rem; background: #1e1e2e; border-bottom: 1px solid #333; font-size: .9rem; color: #9aa; }
  main { padding: 1rem; max-width: 60rem; margin: 0 auto; }
  .crumbs { margin-bottom: 1rem; color: #8ab; } .crumbs .sep { margin: 0 .3rem; color: #556; }
  .crumbs a { color: #7eb8da; text-decoration: none; } .crumbs a:hover { text-decoration: underline; }
  #listing { list-style: none; padding: 0; margin: 0; }
  .row { display: grid; grid-template-columns: 1fr 6rem 9rem auto auto; gap: .5rem; align-items: center; padding: .3rem .4rem; border-bottom: 1px solid #262636; }
  .row:hover { background: #1c1c2a; } .row .name { color: #7eb8da; text-decoration: none; }
  .row .size, .row .date { color: #889; font-size: .85rem; } .row .rename input { width: 8rem; }
  .empty { color: #667; padding: .5rem; }
  .actions { display: flex; gap: 1rem; margin-top: 1rem; }
  input, button, textarea { font: inherit; background: #22223a; color: inherit; border: 1px solid #444; border-radius: 4px; padding: .25rem .5rem; }
  button { cursor: pointer; } button:hover { background: #2c2c48; } .del { color: #e88; }
  textarea { width: 100%; min-height: 60vh; box-sizing: border-box; font-family: ui-monospace, monospace; }
  .bar { margin-top: .5rem; display: flex; gap: 1rem; align-items: center; }
  #status { margin-top: .75rem; min-height: 1.2rem; } .status.ok { color: #8d8; } .status.error { color: #e88; }
  .htmx-request { opacity: .6; }
</style>
<script src="${escapeHtml(htmxSrc)}" integrity="${escapeHtml(htmxIntegrity)}" crossorigin="anonymous"></script>
</head>
<body>
<header>Acequia VFS · <code>${p}</code> · served by the service worker</header>
<main id="main" hx-get="${p}" hx-trigger="load" hx-swap="innerHTML">Loading…</main>
</body>
</html>
`
}

// --- the plugin -------------------------------------------------------------

const FRAGMENT_HEADERS = {
    'Content-Type': 'text/html; charset=utf-8',
    'Cache-Control': 'no-store',
    Vary: 'HX-Request, Accept',
}

export function htmlResponse(body, extra = {}, status = 200) {
    return new Response(body, { status, headers: { ...FRAGMENT_HEADERS, ...extra } })
}

async function readForm(request) {
    const ct = request.headers.get('Content-Type') || ''
    if (ct.includes('multipart/form-data')) return await request.formData()
    return new URLSearchParams(await request.text())
}

/**
 * @param {object} opts
 * @param {{ handle: Function, mountpointFor?: Function }} opts.mount  the mount plugin
 * @param {Function|Array} opts.bindings  shared with the mount plugin
 */
export function createHtmxPlugin({
    mount,
    bindings,
    name = 'htmx',
    order = 15,
    htmxSrc = HTMX_SRC,
    htmxIntegrity = HTMX_INTEGRITY,
    log = () => {},
} = {}) {
    if (!mount || typeof mount.handle !== 'function') throw new Error('swHtmx: createHtmxPlugin needs the mount plugin')

    const rootFor = (url) => (mount.mountpointFor ? mount.mountpointFor(url) : null) || '/'

    const dav = (ctx, url, method, path, { headers = {}, body } = {}) => {
        const target = new URL(path, url)
        const request = new Request(target.toString(), { method, headers, body })
        return mount.handle({ request, waitUntil() {} }, ctx)
    }

    const failure = (what, resp) =>
        htmlResponse(renderStatus(`${what} failed: ${resp ? resp.status + ' ' + (resp.statusText || '') : 'no response'}`.trim(), 'error'), {
            'HX-Retarget': '#status',
            'HX-Reswap': 'innerHTML',
        })

    async function listing(ctx, url, dir, status = '') {
        const resp = await dav(ctx, url, 'GET', dir, { headers: { 'X-Acequia-Directory-JSON': 'true' } })
        if (!resp || !resp.ok) return failure('Listing', resp)
        if (!resp.headers.get('x-acequia-directory-json')) return null // not a directory after all
        const entries = await resp.json()
        return htmlResponse(renderListing({ path: dir, entries, root: rootFor(url), status }))
    }

    async function editor(ctx, url, path, status = '') {
        const resp = await dav(ctx, url, 'GET', path)
        if (!resp) return failure('Read', resp)
        if (resp.status === 404 && !status) return htmlResponse(renderStatus('Not found', 'error'))
        if (!resp.ok) return failure('Read', resp)
        const contentType = resp.headers.get('content-type') || ''
        const len = Number(resp.headers.get('content-length'))
        const root = rootFor(url)
        if (!isTextLike(contentType, path) || len > MAX_EDIT_BYTES) {
            return htmlResponse(renderFileInfo({ path, size: len, contentType, root }))
        }
        const text = await resp.text()
        if (text.length > MAX_EDIT_BYTES) return htmlResponse(renderFileInfo({ path, size: text.length, contentType, root }))
        return htmlResponse(renderEditor({ path, text, etag: resp.headers.get('etag') || '', contentType, root, status }))
    }

    async function handle(ev, ctx) {
        // ?acequia-mounts=off on the page: the mounts, and this layer over
        // them, step aside (sw.js mountsOffFor)
        if (await ctx?.mountsOff?.()) return null
        const { request } = ev
        const url = new URL(request.url)
        const path = url.pathname

        if (!isHtmxRequest(request)) {
            if (wantsHtmlDocument(request)) {
                log('htmx shell:', path)
                return htmlResponse(renderShell({ path, htmxSrc, htmxIntegrity }))
            }
            return null
        }
        log('htmx', request.method, path)

        try {
            switch (request.method) {
                case 'GET': {
                    if (path.endsWith('/')) {
                        const l = await listing(ctx, url, path)
                        if (l) return l
                    }
                    return await editor(ctx, url, path)
                }
                case 'PUT': {
                    const form = await readForm(request)
                    const content = form.get('content') ?? ''
                    const etag = form.get('etag')
                    const headers = { 'Content-Type': form.get('contentType') || 'text/plain; charset=utf-8' }
                    if (etag) headers['If-Match'] = etag
                    const resp = await dav(ctx, url, 'PUT', path, { headers, body: content })
                    if (resp && resp.status === 412) {
                        return htmlResponse(renderStatus('Changed on the server since you opened it — reload before saving.', 'error'), {
                            'HX-Retarget': '#status',
                            'HX-Reswap': 'innerHTML',
                        })
                    }
                    if (!resp || !resp.ok) return failure('Save', resp)
                    return await editor(ctx, url, path, renderStatus('Saved', 'ok')).then((r) => {
                        r.headers.set('HX-Trigger', 'acequia:saved')
                        return r
                    })
                }
                case 'POST': {
                    const form = await readForm(request)
                    const action = form.get('_action')
                    if (action === 'mkcol' || action === 'create') {
                        const name = form.get('name')
                        if (!validName(name)) return failure('Invalid name')
                        const dir = path.endsWith('/') ? path : path + '/'
                        const target = dir + encodeSegment(name) + (action === 'mkcol' ? '/' : '')
                        const resp =
                            action === 'mkcol'
                                ? await dav(ctx, url, 'MKCOL', target)
                                : await dav(ctx, url, 'PUT', target, { headers: { 'Content-Type': 'text/plain; charset=utf-8', 'If-None-Match': '*' }, body: '' })
                        if (!resp || !resp.ok) return failure(action === 'mkcol' ? 'New folder' : 'New file', resp)
                        if (action === 'create') {
                            const r = await editor(ctx, url, target, renderStatus('Created', 'ok'))
                            r.headers.set('HX-Trigger', 'acequia:created')
                            r.headers.set('HX-Push-Url', target)
                            return r
                        }
                        const r = await listing(ctx, url, dir, renderStatus(`Created ${name}/`, 'ok'))
                        if (r) r.headers.set('HX-Trigger', 'acequia:created')
                        return r || failure('Listing')
                    }
                    if (action === 'move') {
                        const destination = form.get('destination')
                        const isDir = path.endsWith('/')
                        const parent = dirOf(path)
                        let destPath
                        if (typeof destination === 'string' && destination.startsWith('/')) destPath = destination
                        else if (validName(destination)) destPath = parent + encodeSegment(destination)
                        else return failure('Invalid destination')
                        if (isDir && !destPath.endsWith('/')) destPath += '/'
                        const resp = await dav(ctx, url, 'MOVE', path, {
                            headers: { Destination: new URL(destPath, url).toString(), Overwrite: 'F' },
                        })
                        if (!resp || !resp.ok) return failure('Move', resp)
                        const r = await listing(ctx, url, parent, renderStatus(`Moved to ${decodeURIComponent(destPath)}`, 'ok'))
                        if (r) r.headers.set('HX-Trigger', 'acequia:moved')
                        return r || failure('Listing')
                    }
                    return failure(`Unknown action ${action}`)
                }
                case 'DELETE': {
                    const resp = await dav(ctx, url, 'DELETE', path)
                    if (!resp || !resp.ok) return failure('Delete', resp)
                    return htmlResponse('', { 'HX-Reswap': 'delete', 'HX-Trigger': 'acequia:deleted' })
                }
                default:
                    return null
            }
        } catch (e) {
            log('htmx error', e)
            return htmlResponse(renderStatus(`Error: ${e.message}`, 'error'), { 'HX-Retarget': '#status', 'HX-Reswap': 'innerHTML' })
        }
    }

    return { name, order, bindings, claim: false, handle }
}
